/*! JointJS+ v4.2.2 (2026-01-22) - HTML5 Diagramming Framework

Copyright (c) 2025 client IO

This Source Code Form is subject to the terms of the JointJS+
License, v. 2.0. If a copy of the JointJS+ License was not
distributed with this file, You can obtain one at
https://www.jointjs.com/license or from the JointJS+ archive as was
distributed by client IO. See the LICENSE file.
*/
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as joint from '@joint/plus';
import { Link, cellNamespace } from '../shapes/app-shapes';
import { ZOOM_SETTINGS } from './navigator-service';
var KitchenSinkService = /** @class */ (function () {
    function KitchenSinkService(el, paperContainer, _a) {
        var stencilService = _a.stencilService, toolbarService = _a.toolbarService, inspectorService = _a.inspectorService, haloService = _a.haloService, keyboardService = _a.keyboardService, navigatorService = _a.navigatorService;
        this.paperContainer = paperContainer;
        this.pageBreakSettings = { color: '#353535', width: 1000, height: 1000 };
        this.defaultPaperSize = { width: 1000, height: 1000 };
        this.stencilService = stencilService;
        this.toolbarService = toolbarService;
        this.inspectorService = inspectorService;
        this.haloService = haloService;
        this.keyboardService = keyboardService;
        this.navigatorService = navigatorService;
        // apply current joint js theme
        joint.setTheme('light');
        var view = new joint.mvc.View({ el: el });
        view.delegateEvents({
            'mouseup input[type="range"]': function (evt) { return evt.target.blur(); }
        });
    }
    KitchenSinkService.prototype.startRappid = function () {
        this.initializePaper();
        this.initializeStencil();
        this.initializeSelection();
        this.initializeToolsAndInspector();
        this.initializeNavigator();
        this.initializeToolbar();
        this.initializeKeyboardShortcuts();
        this.initializeTooltips();
    };
    KitchenSinkService.prototype.initializePaper = function () {
        var _this = this;
        var graph = this.graph = new joint.dia.Graph({}, {
            cellNamespace: cellNamespace
        });
        this.commandManager = new joint.dia.CommandManager({
            graph: graph,
            cmdBeforeAdd: function (cmdName, _cellView, _value, _a) {
                var _b = _a === void 0 ? { ignoreUndoRedo: false } : _a, ignoreUndoRedo = _b.ignoreUndoRedo;
                var _c = cmdName.split(':'), property = _c[1];
                var ignoredChanges = ['infinitePaper', 'dotGrid', 'snaplines', 'gridSize'];
                return !ignoreUndoRedo && !ignoredChanges.some(function (change) { return change === property; });
            }
        });
        var paper = this.paper = new joint.dia.Paper({
            width: 1000,
            height: 1000,
            gridSize: 10,
            drawGrid: true,
            model: graph,
            cellViewNamespace: cellNamespace,
            defaultLink: function () {
                return new Link();
            },
            defaultConnectionPoint: Link.connectionPoint,
            routerNamespace: {
                'normal': joint.routers.normal,
                'orthogonal': joint.routers.orthogonal,
                // Redefine the rightAngle router to use vertices.
                'rightAngle': function (vertices, opt, linkView) {
                    opt.useVertices = true;
                    return joint.routers.rightAngle.call(this, vertices, opt, linkView);
                }
            },
            interactive: { linkMove: false },
            async: true
        });
        paper.on('blank:contextmenu', function (_a, x, y) {
            var clientX = _a.clientX, clientY = _a.clientY;
            var selectionBBox = _this.graph.getCellsBBox(_this.selection.collection.toArray());
            var selectedCells = (selectionBBox === null || selectionBBox === void 0 ? void 0 : selectionBBox.containsPoint({ x: x, y: y })) ? _this.selection.collection.toArray() : [];
            _this.renderContextToolbar({ x: clientX, y: clientY }, selectedCells);
        });
        paper.on('cell:contextmenu', function (cellView, evt) {
            _this.renderContextToolbar({ x: evt.clientX, y: evt.clientY }, [cellView.model]);
        });
        this.snaplines = new joint.ui.Snaplines({ paper: paper });
        var paperScroller = this.paperScroller = new joint.ui.PaperScroller({
            paper: paper,
            autoResizePaper: true,
            scrollWhileDragging: true,
            borderless: true,
            cursor: 'grab'
        });
        var removePageBreaks = null;
        graph.on({
            'change:paperColor': function (_, color) { return paper.drawBackground({ color: color }); },
            'change:infinitePaper': function (_, borderless) {
                var options = paperScroller.options;
                if (borderless) {
                    options.borderless = true;
                    options.baseWidth = 100;
                    options.baseHeight = 100;
                    if (removePageBreaks)
                        removePageBreaks();
                    _this.paperContainer.classList.remove('bordered');
                }
                else {
                    var _a = _this.defaultPaperSize, paperWidth = _a.width, paperHeight = _a.height;
                    options.borderless = false;
                    options.baseWidth = paperWidth;
                    options.baseHeight = paperHeight;
                    removePageBreaks = _this.addPageBreaks();
                    _this.paperContainer.classList.add('bordered');
                }
                paperScroller.adjustPaper();
            },
            'change:dotGrid': function (_, showDotGrid) { return paper.setGrid(showDotGrid); },
            'change:snaplines': function (_, allowSnaplines) { return _this.changeSnapLines(allowSnaplines); },
            'change:gridSize': function (_, gridSize) { return paper.setGridSize(gridSize); },
        });
        this.paperContainer.appendChild(paperScroller.el);
        paperScroller.center();
        paper.on('paper:pan', function (evt, tx, ty) {
            evt.preventDefault();
            paperScroller.el.scrollLeft += tx;
            paperScroller.el.scrollTop += ty;
        });
        paper.on('paper:pinch', function (_evt, ox, oy, scale) {
            // the default is already prevented
            var zoom = paperScroller.zoom();
            paperScroller.zoom(zoom * scale, { min: ZOOM_SETTINGS.min, max: ZOOM_SETTINGS.max, ox: ox, oy: oy, absolute: true });
        });
    };
    KitchenSinkService.prototype.initializeStencil = function () {
        var _this = this;
        var _a = this, stencilService = _a.stencilService, paperScroller = _a.paperScroller, snaplines = _a.snaplines;
        stencilService.create(paperScroller, snaplines);
        stencilService.setShapes();
        stencilService.stencil.on({
            'element:drop': function (elementView) { return _this.selection.collection.reset([elementView.model]); },
            // We need to track the dragging state to prevent showing the tooltip when dragging an element
            'element:dragstart': function () { return _this.tooltip.disable(); },
            'element:dragend': function () { return _this.tooltip.enable(); },
        });
    };
    KitchenSinkService.prototype.initializeSelection = function () {
        var _this = this;
        this.clipboard = new joint.ui.Clipboard();
        this.selection = new joint.ui.Selection({
            boxContent: null,
            paper: this.paperScroller,
            useModelGeometry: true,
            translateConnectedLinks: joint.ui.Selection.ConnectedLinksTranslation.SUBGRAPH,
            handles: [
                __assign(__assign({}, joint.ui.Selection.getDefaultHandle('rotate')), { position: joint.ui.Selection.HandlePosition.SW }),
                __assign(__assign({}, joint.ui.Selection.getDefaultHandle('resize')), { position: joint.ui.Selection.HandlePosition.SE })
            ],
            frames: new joint.ui.HTMLSelectionFrameList({
                rotate: true
            })
        });
        this.selection.collection.on('reset add remove', function () { return _this.onSelectionChange(); });
        var keyboard = this.keyboardService.keyboard;
        // Initiate selecting when the user grabs the blank area of the paper while the Shift key is pressed.
        // Otherwise, initiate paper pan.
        this.paper.on('blank:pointerdown', function (evt, _x, _y) {
            if (keyboard.isActive('shift', evt)) {
                _this.selection.startSelecting(evt);
            }
            else {
                _this.selection.collection.reset([]);
                _this.paperScroller.startPanning(evt);
                _this.paper.removeTools();
            }
        });
        // Initiate selecting when the user grabs a cell while shift is pressed.
        this.paper.on('cell:pointerdown element:magnet:pointerdown', function (cellView, evt) {
            if (keyboard.isActive('shift', evt)) {
                cellView.preventDefaultInteraction(evt);
                _this.selection.startSelecting(evt);
            }
        });
        this.paper.on('element:pointerdown', function (elementView, evt) {
            // Select an element if CTRL/Meta key is pressed while the element is clicked.
            if (keyboard.isActive('ctrl meta', evt)) {
                _this.selection.collection.add(elementView.model);
            }
        });
        this.graph.on('remove', function (cell) {
            // If element is removed from the graph, remove from the selection too.
            if (_this.selection.collection.has(cell)) {
                _this.selection.collection.reset(_this.selection.collection.models.filter(function (c) { return c !== cell; }));
            }
        });
        this.selection.on('selection-box:pointerdown', function (elementView, evt) {
            // Unselect an element if the CTRL/Meta key is pressed while a selected element is clicked.
            if (keyboard.isActive('ctrl meta', evt)) {
                _this.selection.collection.remove(elementView.model);
            }
        }, this);
        this.selection.on('selection-box:pointerup', function (_, evt) {
            if (evt.button === 2) {
                evt.stopPropagation();
                _this.renderContextToolbar({ x: evt.clientX, y: evt.clientY }, _this.selection.collection.toArray());
            }
        }, this);
    };
    KitchenSinkService.prototype.renderContextToolbar = function (point, selectedCells) {
        var _this = this;
        if (selectedCells === void 0) { selectedCells = []; }
        this.selection.collection.reset(selectedCells);
        var isSelectionEmpty = selectedCells.length === 0;
        var contextToolbar = new joint.ui.ContextToolbar({
            target: point,
            root: this.paper.el,
            padding: 0,
            vertical: true,
            anchor: 'top-left',
            tools: [
                {
                    action: 'delete',
                    content: 'Delete',
                    attrs: {
                        disabled: isSelectionEmpty
                    }
                },
                {
                    action: 'copy',
                    content: 'Copy',
                    attrs: {
                        disabled: isSelectionEmpty
                    }
                },
                {
                    action: 'paste',
                    content: 'Paste',
                    attrs: {
                        disabled: this.clipboard.isEmpty()
                    }
                },
                {
                    action: 'send-to-front',
                    content: 'Send to front',
                    attrs: {
                        disabled: isSelectionEmpty
                    }
                },
                {
                    action: 'send-to-back',
                    content: 'Send to back',
                    attrs: {
                        disabled: isSelectionEmpty
                    }
                }
            ]
        });
        contextToolbar.on('action:delete', function () {
            contextToolbar.remove();
            _this.graph.removeCells(selectedCells);
        });
        contextToolbar.on('action:copy', function () {
            contextToolbar.remove();
            _this.clipboard.copyElements(selectedCells, _this.graph);
        });
        contextToolbar.on('action:paste', function () {
            contextToolbar.remove();
            var pastedCells = _this.clipboard.pasteCellsAtPoint(_this.graph, _this.paper.clientToLocalPoint(point));
            var elements = pastedCells.filter(function (cell) { return cell.isElement(); });
            // Make sure pasted elements get selected immediately. This makes the UX better as
            // the user can immediately manipulate the pasted elements.
            _this.selection.collection.reset(elements);
        });
        contextToolbar.on('action:send-to-front', function () {
            contextToolbar.remove();
            selectedCells.forEach(function (cell) { return cell.toFront(); });
        });
        contextToolbar.on('action:send-to-back', function () {
            contextToolbar.remove();
            selectedCells.forEach(function (cell) { return cell.toBack(); });
        });
        contextToolbar.render();
    };
    KitchenSinkService.prototype.onSelectionChange = function () {
        var _a = this, paper = _a.paper, selection = _a.selection;
        var collection = selection.collection;
        paper.removeTools();
        joint.ui.Halo.clear(paper);
        joint.ui.FreeTransform.clear(paper);
        joint.ui.Inspector.close();
        if (collection.length === 1) {
            var primaryCell = collection.first();
            var primaryCellView = paper.findViewByModel(primaryCell);
            selection.destroySelectionBox(primaryCell);
            this.selectPrimaryCell(primaryCellView);
        }
        else if (collection.length === 2) {
            collection.each(function (cell) {
                selection.createSelectionBox(cell);
            });
        }
    };
    KitchenSinkService.prototype.selectPrimaryCell = function (cellView) {
        var cell = cellView.model;
        if (cell.isElement()) {
            this.selectPrimaryElement(cellView);
        }
        else {
            this.selectPrimaryLink(cellView);
        }
        this.inspectorService.create(cell);
    };
    KitchenSinkService.prototype.selectPrimaryElement = function (elementView) {
        var element = elementView.model;
        new joint.ui.FreeTransform({
            cellView: elementView,
            allowRotation: false,
            preserveAspectRatio: !!element.get('preserveAspectRatio'),
            allowOrthogonalResize: element.get('allowOrthogonalResize') !== false,
            useBordersToResize: true
        }).render();
        this.haloService.create(elementView);
    };
    KitchenSinkService.prototype.selectPrimaryLink = function (linkView) {
        var ns = joint.linkTools;
        var tools = [
            new ns.Vertices(),
            new ns.SourceAnchor(),
            new ns.TargetAnchor(),
            new ns.SourceArrowhead(),
            new ns.TargetArrowhead(),
            new ns.Segments({
                visibility: function (linkView) {
                    return linkView.model.router().name === 'normal';
                }
            }),
            new ns.Boundary({ padding: 15 }),
            new ns.Remove({ offset: -20, distance: 40 })
        ];
        var toolsView = new joint.dia.ToolsView({
            name: 'link-pointerdown',
            tools: tools
        });
        linkView.addTools(toolsView);
    };
    KitchenSinkService.prototype.initializeToolsAndInspector = function () {
        var _this = this;
        this.paper.on('cell:pointerup', function (cellView) {
            var cell = cellView.model;
            var collection = _this.selection.collection;
            if (collection.includes(cell)) {
                return;
            }
            collection.reset([cell]);
        });
        this.paper.on('link:mouseenter', function (linkView) {
            // Open tool only if there is none yet
            if (linkView.hasTools()) {
                return;
            }
            var ns = joint.linkTools;
            var toolsView = new joint.dia.ToolsView({
                name: 'link-hover',
                tools: [
                    new ns.Vertices({ vertexAdding: false }),
                    new ns.SourceArrowhead(),
                    new ns.TargetArrowhead()
                ]
            });
            linkView.addTools(toolsView);
        });
        this.paper.on('link:mouseleave', function (linkView) {
            // Remove only the hover tool, not the pointerdown tool
            if (linkView.hasTools('link-hover')) {
                linkView.removeTools();
            }
        });
        this.graph.on('change', function (cell, opt) {
            if (cell instanceof joint.dia.Graph || !cell.isLink() || !opt.inspector) {
                return;
            }
            var ns = joint.linkTools;
            var toolsView = new joint.dia.ToolsView({
                name: 'link-inspected',
                tools: [
                    new ns.Boundary({ padding: 15 }),
                ]
            });
            cell.findView(_this.paper).addTools(toolsView);
        });
    };
    KitchenSinkService.prototype.initializeNavigator = function () {
        this.navigatorService.create(this.paperScroller);
    };
    KitchenSinkService.prototype.initializeToolbar = function () {
        this.toolbarService.create(this.commandManager, this.paperScroller, this.graph, this.paper);
    };
    KitchenSinkService.prototype.applyOnSelection = function (method) {
        this.graph.startBatch('selection');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.selection.collection.models.forEach(function (model) { model[method](); });
        this.graph.stopBatch('selection');
    };
    KitchenSinkService.prototype.changeSnapLines = function (checked) {
        if (checked) {
            this.snaplines.enable();
        }
        else {
            this.snaplines.disable();
        }
    };
    KitchenSinkService.prototype.initializeKeyboardShortcuts = function () {
        this.keyboardService.create(this.graph, this.clipboard, this.selection, this.paperScroller, this.commandManager);
    };
    KitchenSinkService.prototype.initializeTooltips = function () {
        this.tooltip = new joint.ui.Tooltip({
            rootTarget: document.body,
            target: '[data-tooltip]',
            direction: joint.ui.Tooltip.TooltipArrowPosition.Auto,
            padding: 12,
            animation: {
                delay: '250ms'
            }
        });
    };
    KitchenSinkService.prototype.addPageBreaks = function () {
        var _a = this, paper = _a.paper, pageBreakSettings = _a.pageBreakSettings;
        var color = pageBreakSettings.color, width = pageBreakSettings.width, height = pageBreakSettings.height;
        var pageBreaksVEl = joint.V('path', {
            stroke: color,
            fill: 'none',
            strokeDasharray: '5,5'
        });
        paper.layers.prepend(pageBreaksVEl.node);
        var lastArea = null;
        function updatePageBreaks() {
            var area = paper.getArea();
            // Do not update if the area is the same
            if (area.equals(lastArea))
                return;
            lastArea = area;
            var d = '';
            // Draw vertical lines
            // Do not draw the first and last lines
            for (var x = width; x < area.width; x += width) {
                d += "M ".concat(area.x + x, " ").concat(area.y, " v ").concat(area.height);
            }
            // Draw horizontal lines
            // Do not draw the first and last lines
            for (var y = height; y < area.height; y += height) {
                d += " M ".concat(area.x, " ").concat(area.y + y, " h ").concat(area.width);
            }
            pageBreaksVEl.attr('d', d || null);
        }
        updatePageBreaks();
        paper.on('translate resize', updatePageBreaks);
        return function () {
            paper.off('translate resize', updatePageBreaks);
            pageBreaksVEl.remove();
        };
    };
    return KitchenSinkService;
}());
export default KitchenSinkService;
